import type { Row } from '@tanstack/react-table'
import { useChatbotAttachments } from 'features/chatbot'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import type { SearchStateUpdate } from 'features/knowledgeDocumentViewer/types'
import React, { useCallback, useState } from 'react'

interface UseDocumentResultsInteractionsParams {
  onSearchDocumentNavigate: (
    doc: DocumentTableRow,
    newTab: boolean,
    isLinkedDocsMode: boolean
  ) => void
  onOpenChatbot?: () => void
  onOpenPreview?: () => void
  onSearchStateChange: (updates: SearchStateUpdate) => void
}

export interface UseDocumentResultsInteractionsResult {
  contextMenuPosition: { x: number; y: number } | null
  handleAddToContext: (doc: DocumentTableRow) => void
  handleRowClick: (
    row: Row<DocumentTableRow>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void
  handleRowAuxClick: (
    row: Row<DocumentTableRow>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void
  handleRowContextMenu: (
    row: Row<DocumentTableRow>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void
  handleContextMenuClose: () => void
  handleContextMenuOpen: () => void
  handleContextMenuOpenInNewTab: () => void
  handleContextMenuAddToContext: () => void
}

export function useDocumentResultsInteractions({
  onSearchDocumentNavigate,
  onOpenChatbot,
  onOpenPreview,
  onSearchStateChange,
}: UseDocumentResultsInteractionsParams): UseDocumentResultsInteractionsResult {
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    x: number
    y: number
  } | null>(null)
  const [contextMenuDocument, setContextMenuDocument] =
    useState<DocumentTableRow | null>(null)

  const { addAttachment, attachments } = useChatbotAttachments()

  const handleAddToContext = useCallback(
    (doc: DocumentTableRow) => {
      const alreadyAttached = attachments.some(
        (attachment) => attachment.id === doc.id
      )
      if (!alreadyAttached) {
        addAttachment({
          id: doc.id,
          type: 'system_document',
          name: doc.filename ?? doc.id,
          displayTitle: doc.title ?? doc.filename ?? doc.id,
        })
      }
      onOpenChatbot?.()
    },
    [addAttachment, attachments, onOpenChatbot]
  )

  const handleOpenDocument = useCallback(
    (doc: DocumentTableRow) => {
      onSearchDocumentNavigate(doc, false, false)
    },
    [onSearchDocumentNavigate]
  )

  const handleOpenInNewTab = useCallback(
    (doc: DocumentTableRow) => {
      onSearchDocumentNavigate(doc, true, false)
    },
    [onSearchDocumentNavigate]
  )

  const handleRowClick = useCallback(
    (
      row: Row<DocumentTableRow>,
      event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
    ) => {
      onOpenPreview?.()
      onSearchStateChange({
        selectedDocumentId: row.original.id,
      })

      if (event.detail === 2) {
        if (event.ctrlKey || event.metaKey) {
          handleOpenInNewTab(row.original)
        } else {
          handleOpenDocument(row.original)
        }
      }
    },
    [handleOpenDocument, handleOpenInNewTab, onOpenPreview, onSearchStateChange]
  )

  const handleRowAuxClick = useCallback(
    (
      row: Row<DocumentTableRow>,
      event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
    ) => {
      if (event.button === 1) {
        event.preventDefault()
        onSearchDocumentNavigate(row.original, true, false)
      }
    },
    [onSearchDocumentNavigate]
  )

  const handleRowContextMenu = useCallback(
    (
      row: Row<DocumentTableRow>,
      event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
    ) => {
      event.preventDefault()
      setContextMenuDocument(row.original)
      setContextMenuPosition({ x: event.clientX, y: event.clientY })
    },
    []
  )

  const handleContextMenuClose = useCallback(() => {
    setContextMenuPosition(null)
    setContextMenuDocument(null)
  }, [])

  const handleContextMenuOpen = useCallback(() => {
    if (contextMenuDocument) {
      handleOpenDocument(contextMenuDocument)
    }
  }, [contextMenuDocument, handleOpenDocument])

  const handleContextMenuOpenInNewTab = useCallback(() => {
    if (contextMenuDocument) {
      handleOpenInNewTab(contextMenuDocument)
    }
  }, [contextMenuDocument, handleOpenInNewTab])

  const handleContextMenuAddToContext = useCallback(() => {
    if (contextMenuDocument) {
      handleAddToContext(contextMenuDocument)
    }
  }, [contextMenuDocument, handleAddToContext])

  return {
    contextMenuPosition,
    handleAddToContext,
    handleRowClick,
    handleRowAuxClick,
    handleRowContextMenu,
    handleContextMenuClose,
    handleContextMenuOpen,
    handleContextMenuOpenInNewTab,
    handleContextMenuAddToContext,
  }
}
